import React, { useState } from 'react';
import { 
  PieChart, 
  Pie, 
  Cell, 
  ResponsiveContainer, 
  Tooltip, 
  Legend, 
  Sector, 
  SectorProps,
  PieProps
} from 'recharts';
import { formatCurrencyAmount, reduceDataIntoSingleComprehensivePolicyObjectPlusOtherPoliciesForPieCharts, removeUnderscoresAndCapitalize, stepsAndDataPerStep } from '../../../../../utils/miscFunctions';
import ChartArrowsCommand from '../../../../../components/form-control/ChartArrowsCommand';
import { MoveLeftIcon, MoveRightIcon } from 'lucide-react';

// Types
export interface IChartItem {
  count: number;
  label: string;
  percentage: number;
  value: number;
  color?: string;
}

export interface ICommissionsDistributionData {
  title: string;
  items: IChartItem[];
  total: number;
  currency: string;
}

interface CommissionDistributionChartsProps {
  insurerDistributionData: ICommissionsDistributionData;
  policyTypeDistributionData: ICommissionsDistributionData;
  isLoading?: boolean;
  error?: string | null;
}

const COLORS = [
  '#10B981', // emerald-500
  '#F59E0B', // amber-500
  '#EF4444', // red-500
  '#8B5CF6', // violet-500
  '#EC4899', // pink-500
  '#14B8A6', // teal-500
  '#F97316', // orange-500
];

const EmptyState: React.FC<{ icon: string; title: string; description: string }> = ({ 
  icon, 
  title, 
  description 
}) => (
  <div className="flex flex-col items-center justify-center h-64 p-6 text-center">
    <div className="w-16 h-16 bg-blue-50 dark:bg-blue-900/30 rounded-full flex items-center justify-center mb-4">
      <span className="text-2xl">{icon}</span>
    </div>
    <h3 className="text-lg font-medium text-gray-900 dark:text-darkBg-mainText mb-1">{title}</h3>
    <p className="text-sm text-gray-500 dark:text-darkBg-subText max-w-xs">{description}</p>
  </div>
);

const LoadingState: React.FC = () => (
  <div className="flex items-center justify-center h-64">
    <div className="animate-pulse flex flex-col items-center">
      <div className="w-32 h-32 rounded-full bg-gray-200 dark:bg-darkBg-border mb-4"></div>
      <div className="h-4 bg-gray-200 dark:bg-darkBg-border rounded w-3/4 mb-2"></div>
      <div className="h-4 bg-gray-200 dark:bg-darkBg-border rounded w-1/2"></div>
    </div>
  </div>
);

interface ActiveShapeProps extends SectorProps {
  outerRadius: number;
  cx?: number;
  cy?: number;
  innerRadius: number;
  startAngle: number;
  endAngle: number;
  fill: string;
}

const ActiveShape = ({
  cx = 0,
  cy = 0,
  innerRadius,
  outerRadius,
  startAngle,
  endAngle,
  fill,
}: ActiveShapeProps) => (
  <g>
    <Sector
      cx={cx}
      cy={cy}
      innerRadius={innerRadius}
      outerRadius={outerRadius + 6}
      startAngle={startAngle}
      endAngle={endAngle}
      fill={fill}
    />
    <Sector
      cx={cx}
      cy={cy}
      startAngle={startAngle}
      endAngle={endAngle}
      innerRadius={outerRadius + 8}
      outerRadius={outerRadius + 10}
      fill={fill}
    />
  </g>
);

const CustomTooltip: React.FC<any> = ({ active, payload }) => {
  if (!active || !payload || !payload.length) return null;
  
  const data = payload[0].payload;
  return (
    <div className="bg-white dark:bg-darkBg p-3 border border-gray-200 dark:border-darkBg-border shadow-md rounded-md text-sm">
      <p className="font-medium text-gray-900 dark:text-darkBg-mainText">{data.label}</p>
      <p className="text-gray-600 dark:text-darkBg-subText">Commission: {formatCurrencyAmount(data.value)}</p>
      {data.count && <p className="text-gray-600 dark:text-darkBg-subText">Policies: {data.count}</p>}
      <p className="text-gray-600 dark:text-darkBg-subText">Share: {data.percentage?.toFixed(1) || '0.0'}%</p>
    </div>
  );
};

const CommissionDistributionCharts: React.FC<CommissionDistributionChartsProps> = ({
  insurerDistributionData,
  policyTypeDistributionData,
  isLoading = false,
  error = null,
}) => {
  const [activeIndex, setActiveIndex] = useState<Record<string, number | null>>({
    policy: null,
    insurer: null,
  });
  
  const x = 4
  const [step, setStep] = useState(0)
  const [step2, setStep2] = useState(0)


const policyTypeList = (data: any, chartStep: number) => {
  if (!data) return [];

  const start = chartStep * x;
  const end = start + x;
  return data.slice(start, end);
};

  const prepareChartData = (items: IChartItem[]) => 
    items.map((item, index) => ({
      ...item,
      color: COLORS[index % COLORS.length],
    }));

  const policyTypeData = React.useMemo(
    () => prepareChartData(policyTypeDistributionData.items),
    [policyTypeDistributionData.items]
  );
  
  const finalDataForPolicies = reduceDataIntoSingleComprehensivePolicyObjectPlusOtherPoliciesForPieCharts(policyTypeData,'#3B82F6');

  const insurerData = React.useMemo(
    () => prepareChartData(insurerDistributionData.items),
    [insurerDistributionData.items]
  );

  const hasPolicyData = policyTypeData.length > 0 && policyTypeData.some(item => item.value > 0);
  const hasInsurerData = insurerData.length > 0 && insurerData.some(item => item.value > 0);

  const totalNumberOfPolicyTypes = policyTypeData?.length || 0;
  const totalNumberOfInsurers = insurerData?.length || 0;
  const NumberOfStepsForPolicyTypes = Math.ceil(totalNumberOfPolicyTypes / x)
  const NumberOfStepsForInsurers = Math.ceil(totalNumberOfInsurers / x)

  if (isLoading) {
    return (
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <LoadingState />
        <LoadingState />
      </div>
    );
  }

  if (error) {
    return (
      <EmptyState 
        icon="⚠️" 
        title="Error loading data" 
        description="We couldn't load the commission data. Please try again later." 
      />
    );
  }


  const renderPieChart = (data: IChartItem[], dataKey: 'policy' | 'insurer'): React.ReactElement => {
    const isPolicy = dataKey === 'policy';
    const chartData = isPolicy ? finalDataForPolicies : insurerData;
    const activeIndexKey = isPolicy ? 'policy' : 'insurer';
    if (!chartData.length) {
      return <div key={`empty-${dataKey}`} className="h-full flex items-center justify-center text-gray-500 dark:text-darkBg-subText">No data available</div>;
    }
    
   // setTotalNumberOfPolicyTypes(chartData.length)
   const dataLabels = isPolicy ? policyTypeList(finalDataForPolicies, step) : policyTypeList(insurerData, step2)
    return (
      <PieChart>
        <Pie
          activeIndex={activeIndex[activeIndexKey] ?? undefined}
          activeShape={ActiveShape as any}
          data={chartData}
          cx="50%"
          cy="50%"
          innerRadius={60}
          outerRadius={80}
          paddingAngle={2}
          dataKey="value"
          nameKey="label"
          onMouseEnter={(_, index) => 
            setActiveIndex(prev => ({ ...prev, [activeIndexKey]: index }))
          }
          onMouseLeave={() => 
            setActiveIndex(prev => ({ ...prev, [activeIndexKey]: null }))
          }
        >
          {chartData.map((entry, index) => (
            <Cell key={`cell-${index}`} fill={entry.color || COLORS[index % COLORS.length]} />
          ))}
        </Pie>
        <Tooltip content={<CustomTooltip />} />
        <Legend
           content={()=>{
                const dataset = dataLabels || [];
               
               return (
                <ul className="flex flex-wrap justify-center text-xs text-gray-700 dark:text-darkBg-mainText">
                  {dataset.map((item:any, idx:number) => (
                    <li key={idx} className="mx-2 flex items-center">
                      <span
                        className="inline-block w-2 h-2 rounded-full mr-1"
                        style={{ backgroundColor: item.color }}
                      />
                      {item.label}
                    </li>
                  ))}
                </ul>
              );}
            }
        />
      </PieChart>
    );
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Policy Type Distribution */}
      <div className="bg-white dark:bg-darkBg rounded-2xl shadow-sm border border-gray-200 dark:border-darkBg-border p-6">
        <div className="mb-4 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-darkBg-mainText">
              {/* {coverTypeDistributionData.title} */}
              Policy Types
            </h2>
            <p className="text-sm text-gray-600 dark:text-darkBg-subText">Distribution across policy types</p>
          </div>
        
        </div>

        <div className="h-64">
          {hasPolicyData && (
            <>
             <ResponsiveContainer width="100%" height="100%">
              {renderPieChart(policyTypeData, 'policy')}
            </ResponsiveContainer>

              <div className='flex items-center justify-center w-full'>
                <div className={`flex gap-3`}>
                  <MoveLeftIcon onClick={()=>{step > 0 && setStep(step-1)}} className={`${step === 0 ? 'text-gray-100 dark:text-darkBg-border' : 'text-gray-500 dark:text-darkBg-subText'} transition-all duration-300 scale-100 hover:scale-110 cursor-pointer`}/>
                  <MoveRightIcon onClick={()=>{step < NumberOfStepsForPolicyTypes-1 && setStep(step+1)}} className={`${step === NumberOfStepsForPolicyTypes-1 ? 'text-gray-100 dark:text-darkBg-border' : 'text-gray-500 dark:text-darkBg-subText'} transition-all duration-300 scale-100 hover:scale-110 cursor-pointer`}/>
                </div>
              </div>
            </>
           
          )}
          {!hasPolicyData && (
            <EmptyState 
              icon="📊"
              title="No policy type data"
              description="Commission data by policy type will appear here once available"
            />
          )}
        </div>
      </div>

      {/* Insurance Company Distribution */}
      <div className="bg-white dark:bg-darkBg rounded-2xl shadow-sm border border-gray-200 dark:border-darkBg-border p-6">
        <div className="mb-4 flex items-center justify-between">
          <div>
             <h2 className="text-lg font-semibold text-gray-900 dark:text-darkBg-mainText">
            {insurerDistributionData.title}
          </h2>
          <p className="text-sm text-gray-600 dark:text-darkBg-subText">Distribution across insurance companies</p>
          </div>
         
        </div>

        <div className="h-64">
          {hasInsurerData && (
            <>
              <ResponsiveContainer width="100%" height="100%">
                {renderPieChart(insurerData, 'insurer')}
              </ResponsiveContainer>
              <div className='flex items-center justify-center w-full'>
                <div className={`flex gap-3`}>
                    <MoveLeftIcon onClick={()=>{step2 > 0 && setStep2(step2-1)}} className={`${step2 === 0 ? 'text-gray-100 dark:text-darkBg-border' : 'text-gray-500 dark:text-darkBg-subText'} transition-all duration-300 scale-100 hover:scale-110 cursor-pointer`}/>
                    <MoveRightIcon onClick={()=>{step2 < NumberOfStepsForInsurers-1 && setStep2(step2+1)}} className={`${step2 === NumberOfStepsForInsurers-1 ? 'text-gray-100 dark:text-darkBg-border' : 'text-gray-500 dark:text-darkBg-subText'} transition-all duration-300 scale-100 hover:scale-110 cursor-pointer`}/>
                </div>
              </div>
            </>
          )}
          {!hasInsurerData && (
            <EmptyState 
              icon="🏢"
              title="No insurer data"
              description="Commission data by insurer will appear here once available"
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default CommissionDistributionCharts;